/**
 * @fileOverview Base class for all dropdowns
 * @author Selectra Front End Team
 * @version 1.0.0
 * @requires Polyfill:Element.classList
 * @requires Polyfill:String.codePointAt
 * @requires Polyfill:String.fromCodePoint
 * @requires Polyfill:CustomEvent
 */


/**
 * Represents a generic dropdown
 * @exports
 */
export default class DropdownBase {
  /**
   * Create a generic dropdown
   * @param { object } element - A reference to the nav or select object
   * @param { object } options - A configuration object
   */
  constructor( baseElement, options, index, manager ) {
    this._configure( options );

    this.index = index;
    this.manager = manager;
    this.type = null;

    this.baseEl = baseElement;
    this.trigger = null;

    this.list = {
      el: null,
      selected: null,
      isShown: false,
      isActive: true,
      waypoints: this.opt.createWaypoints ? {} : null,
      html: document.createDocumentFragment()
    };

  }


  /**
   * @param { object } options - A configuration object
   * @private
   */
  _configure( options ) {
    const defaults = {
      sortList: true,
      createWaypoints: true,
      spritePath: '/img/sprite.svg',
    };
    this.opt = { ...defaults, ...options };
  }


  /**
   * Set-up Dropdown
   * @private
   */
  _buildCommon() {

    const
      itemsToSort = [],
      list = this.list.html.firstChild;

    // -- Set tabindex of dropdown trigger
    if ( !this.list.isActive ) {
      this.trigger.setAttribute( 'tabindex', -1 );
    }

    this.trigger.setAttribute( 'type', 'button' );

    // -- Set list tabindex
    this.list.html.firstChild.setAttribute( 'tabindex', -1 );

    Array.from( list.children ).forEach( element => {
      // -- Set tabindex of list elements
      this.list.isActive ?
        element.setAttribute( 'tabindex', '0' ) :
        element.setAttribute( 'tabindex', '-1' );

      // -- Set aria attributes
      element.setAttribute( 'role', 'option' );

      // -- Bail out on first disabled element
      //    We do not use it as a normal dropdown option
      if ( element.hasAttribute( 'disabled' ) &&
        element === element.parentElement.firstChild ) {
        return;
      }

      // -- Create list items objects
      if ( this.opt.sortList ) {
        itemsToSort.push(
          {
            listElement: element,
            value: element.textContent.toUpperCase(),
          }
        );
      }
    });

    if ( this.opt.sortList ) {

      // -- Sort list items
      itemsToSort.sort(
        ( firstEl, secondEl ) => {
          return firstEl.value.localeCompare(
            secondEl.value, undefined, { numeric: true, sensivity: 'base' }
          )
        });

      let currLetter = '',
        lastLetter = '';

      itemsToSort.forEach( item => {
        // -- Populate the waypoints object
        // We'll use it later for keyboard navigation :)
        if ( this.opt.createWaypoints &&
            !item.listElement.hasAttribute( 'disabled' )) {
          currLetter = item.value[ 0 ];
          if ( !currLetter ) {
            return;
          } else if ( currLetter !== lastLetter ) {
            this.list.waypoints[ currLetter.codePointAt( 0 ) ] =
              item.listElement;
          }
          lastLetter = currLetter;
        }

        // -- Reattach list elements
        list.appendChild( item.listElement );
      });

    }

    // -- Set aria attributes
    this.trigger.setAttribute( 'aria-haspopup', 'listbox' );
    this.list.el.setAttribute( 'role', 'listbox' );

    // -- Append list to DOM
    this.baseEl.appendChild( this.list.html );

    delete this.list.html;

  }


  /**
   * Display dropdown list
   */
  showList() {
    this.baseEl.classList.add( 'js--active' );
    this.list.isShown = true;
    this.trigger.setAttribute( 'aria-expanded', 'true' );
  }


  /**
   * Hide dropdown list
   */
  hideList() {
    this.baseEl.classList.remove( 'js--active' );
    this.list.isShown = false;
    this.trigger.removeAttribute( 'aria-expanded' );
  }


  /**
   * Toggle list display status
   */
  toggleList() {

    if ( this.list.isActive ) {
      this.list.isShown ? this.hideList() : this.showList();
      return this.list.isShown;
    } else {
      return false;
    }

  }

  /**
   * @method remove
   * @description Delete dropdown references and it's markup.
   */
  remove() {

    const
      dropdowns = this.manager.dropdowns,
      thisIdx = this.index,
      thisEl = this.baseEl;

    // Make sure it's closed.
    this.toggleList();

    // Remove dropdown markup.
    thisEl.parentElement.removeChild( thisEl );
    delete dropdowns.splice( this.index, 1 );

    let totalDropdowns = dropdowns.length;

    // Reset index for subsequent dropdowns in the list.
    for ( let i = thisIdx; i < totalDropdowns; ++i ) {
      dropdowns[ i ].index = i;
    }

  }


  /**
   * Set eventListeners
   * @private
   */
  _setCommonEventListeners() {

    const

      /**
       * Dispatch custom click event
       * @param { object } e -- event
       */
      dispatchCustomClick = e => {
        e.preventDefault();
        e.stopPropagation();
        this.trigger.dispatchEvent(
          new CustomEvent(
            'btn_click',
            { bubbles: true,
              detail: this.index }
          )
        );
      },

      /**
       * Dispatch custom focus event
       * @param { object } e -- event
       */
      dispatchCustomFocus = e => {
        e.stopPropagation();
        this.trigger.dispatchEvent(
          new CustomEvent(
            'btn_focus',
            { bubbles: true,
              detail: this.index, }
          )
        );
      },

      /**
     * Update selected list element
     * @param { object } e -- event
     */
      updateSelected = e => {
        this.list.selected = e.target;
      },

      /**
     * Keyboard control
     * @param { object } e -- event
     */
      processKey = e => {

        const keyCode = e.which;
        let focusElement,
          keyCanNavigate;

        if ( this.list.isShown ) {

          const
            getNextActiveItem = item => {

              while ( item.hasAttribute( 'disabled' )) {
                if ( item === this.list.el.lastElementChild ) {
                  item = this.list.el.firstElementChild;
                  break;
                }
                item = item.nextElementSibling;
              }

              return item;
            },
            getPreviousActiveItem = item => {

              while ( item.hasAttribute( 'disabled' )) {
                if ( item === this.list.el.firstElementChild ) {
                  item = this.list.el.lastElementChild;
                  break;
                }
                item = item.previousElementSibling;
              }

              return item;
            }

          switch ( keyCode ) {

          case 9:
            // Key Tab
            break;

          case 13:
            // Key Enter
            if ( this.list.selected ) {
              this._activateSelected();
            }
            break;

          case 27:
            // Key Escape
            this.hideList();
            break;

          case 35:
            // Key End
            focusElement = this.list.el.lastElementChild;
            getPreviousActiveItem( focusElement ).focus();
            break;

          case 36:
            // Key Home
            focusElement = this.list.el.firstElementChild;
            getNextActiveItem( focusElement ).focus();
            break;

          case 38:
            // Arrow UP
            e.preventDefault();

            if ( this.list.selected === this.list.el.firstElementChild ) {
              focusElement = this.list.el.lastElementChild;
            } else {
              focusElement = this.list.selected.previousElementSibling;
            }
            getPreviousActiveItem( focusElement ).focus();
            break;

          case 40:
            // Arrow DOWN
            e.preventDefault();

            if ( this.list.selected === this.list.el.lastElementChild ) {
              focusElement = this.list.el.firstElementChild;
            } else {
              focusElement = this.list.selected.nextElementSibling;
            }
            getNextActiveItem( focusElement ).focus();
            break;


          default:
            // Alphabetical navigation
            keyCanNavigate =
              // Number keys
              ( keyCode > 47 && keyCode < 58 ) ||
              // Letter keys
              ( keyCode > 64 && keyCode < 91 );

            if ( keyCanNavigate ) {

              const
                waypoint = this.list.waypoints[ keyCode ],
                nextElement = this.list.selected.nextElementSibling,
                nextElementCode =
                nextElement.textContent.codePointAt( 0 ),
                focusWaypoint = waypoint &&
                                waypoint !== this.list.selected &&
                                nextElementCode !== keyCode,
                focusNextElement = nextElementCode === keyCode;

              if ( focusWaypoint ) {
                this.list.selected = this.list.waypoints[ keyCode ];
              } else if ( focusNextElement ) {
                this.list.selected = nextElement;
              }

              this.list.selected.focus();

            }

            break;

          }
        }
      }


    // -- Set callback functions
    this.trigger.addEventListener( 'mousedown', dispatchCustomClick );
    this.trigger.addEventListener( 'focus', dispatchCustomFocus );

    this.baseEl.addEventListener( 'keydown', processKey );
    this.baseEl.addEventListener( 'focusin', updateSelected );

  }

}
